import { linkResolver } from "src/lib/linkResolver";
import { HeaderSection } from "./types";

export const groupSections = ({ body }: Gatsby.SiteNavbarV2BodyFragment): HeaderSection[] | undefined => {
  const groupedSections = body?.reduce((sections, curr) => {
    //prisimic sections types identify its nested level and are indexed by its order
    const currentSection = sections[sections.length - 1];

    if (curr?.__typename === "PrismicSiteNavbarV2DataBodyHeaderSection") {
      //Appearance of the header section(top level): indicates the beginning of new top level section,
      //and will nest any 2nd level items until the apprearance of new header section
      sections.push(curr);
    }

    //Dropdown column and advertisement (2nd level): are nested inside the current top level section
    // and will nest any 3rd level items until the appearance of new 2nd level section

    //length of top level sections will not change in this step, but the length of section columns will change in this step
    if (
      curr?.__typename === "PrismicSiteNavbarV2DataBodyDropdownColumn" ||
      curr?.__typename === "PrismicSiteNavbarV2DataBodyAdvertisement"
    ) {
      if (currentSection) {
        if (currentSection.columns && !currentSection.columns.includes(curr)) {
          currentSection.columns = [...currentSection.columns, curr]; //if current section has exisiting columns array, insert itself
        } else {
          currentSection.columns = [curr]; // if current section has no nested columns array, create columns array
        }
      }
    }

    //Dropdown panel (3rd level): is nested in column(2nd level)
    //length of top level sections or current section's columns will not change, but the length of current section's current column panels will change
    if (curr?.__typename === "PrismicSiteNavbarV2DataBodyDropdownPanel") {
      const currentColumn =
        currentSection.columns &&
        currentSection.columns[currentSection.columns?.length - 1];

      if (currentColumn) {
        if (currentColumn.panels && !currentColumn.panels.includes(curr)) {
          currentColumn.panels = [...currentColumn.panels, curr]; //if current column has existing panels, insert itset
        } else {
          currentColumn.panels = [curr]; //if current section has no nested panels array, create panels array
        }
      }
    }

    return sections;
  }, [] as HeaderSection[]);

  return groupedSections;
};

export const groupSectionsV3 = ({ body }: Gatsby.SiteNavbarV3BodyFragment): HeaderSection[] | undefined => {
  const groupedSections = body?.reduce((sections, curr) => {
    //prisimic sections types identify its nested level and are indexed by its order
    const currentSection = sections[sections.length - 1];

    if (curr?.__typename === "PrismicSiteNavbarV3DataBodyHeaderSection") {
      //Appearance of the header section(top level): indicates the beginning of new top level section,
      //and will nest any 2nd level items until the apprearance of new header section
      sections.push(curr);
    }

    //Dropdown column and advertisement (2nd level): are nested inside the current top level section
    // and will nest any 3rd level items until the appearance of new 2nd level section

    //length of top level sections will not change in this step, but the length of section columns will change in this step
    if (
      curr?.__typename === "PrismicSiteNavbarV3DataBodyDropdownColumn" ||
      curr?.__typename === "PrismicSiteNavbarV3DataBodyAdvertisement"
    ) {
      if (currentSection) {
        if (currentSection.columns && !currentSection.columns.includes(curr)) {
          currentSection.columns = [...currentSection.columns, curr]; //if current section has exisiting columns array, insert itself
        } else {
          currentSection.columns = [curr]; // if current section has no nested columns array, create columns array
        }
      }
    }

    //Dropdown panel (3rd level): is nested in column(2nd level)
    //length of top level sections or current section's columns will not change, but the length of current section's current column panels will change
    if (curr?.__typename === "PrismicSiteNavbarV3DataBodyDropdownPanel") {
      const currentColumn =
        currentSection.columns &&
        currentSection.columns[currentSection.columns?.length - 1];

      if (currentColumn) {
        if (currentColumn.panels && !currentColumn.panels.includes(curr)) {
          currentColumn.panels = [...currentColumn.panels, curr]; //if current column has existing panels, insert itset
        } else {
          currentColumn.panels = [curr]; //if current section has no nested panels array, create panels array
        }
      }
    }

    return sections;
  }, [] as HeaderSection[]);

  return groupedSections;
};

export const groupSectionsV5 = ({ body }: any): HeaderSection[] | undefined => {
  const groupedSections = body?.reduce((sections: any, curr: any) => {
    const currentSection = sections[sections.length - 1];

    if (curr?.__typename === "PrismicSiteNavbarV5DataBodyHeaderSection") {
      sections.push(curr);
    }

    if (
      curr?.__typename === "PrismicSiteNavbarV5DataBodyDropdownColumn" ||
      curr?.__typename === "PrismicSiteNavbarV5DataBodyAdvertisement"
    ) {
      if (currentSection) {
        if (currentSection.columns && !currentSection.columns.includes(curr)) {
          currentSection.columns = [...currentSection.columns, curr];
        } else {
          currentSection.columns = [curr];
        }
      }
    }

    if (curr?.__typename === "PrismicSiteNavbarV5DataBodyDropdownPanel") {
      const currentColumn =
        currentSection.columns &&
        currentSection.columns[currentSection.columns?.length - 1];

      if (currentColumn) {
        if (currentColumn.panels && !currentColumn.panels.includes(curr)) {
          currentColumn.panels = [...currentColumn.panels, curr];
        } else {
          currentColumn.panels = [curr];
        }
      }
    }

    return sections;
  }, [] as HeaderSection[]);

  return groupedSections;
};

export const resolveNavVersion = (
  rawVersion: string | null | undefined,
): "v2" | "v3" | "v4" | "v5" | "global-business-nav" | "global-accountant-nav" | undefined => {
  if (!rawVersion) return undefined;
  const lowercaseVersion = rawVersion.toLowerCase();

  if (lowercaseVersion.includes("global-business-nav")) return "global-business-nav";
  if (lowercaseVersion.includes("global-accountant-nav")) return "global-accountant-nav";
  if (lowercaseVersion.includes("v5")) return "v5";
  if (lowercaseVersion.includes("v4")) return "v4";
  if (lowercaseVersion.includes("v3")) return "v3";

  return "v2";
};

type HeroSlice = {
  slice_type: "header" | "header_spotlight";
  primary?: {
    buttons?: { raw?: Array<any> };
    content?: { raw?: Array<any> };
  };
};

export const isHeroWithMatchingButton = (
  slice: HeroSlice | null | undefined | any,
  startLabel: string,
  startUrl: string,
): boolean => {
  if (
    !slice ||
    (slice.slice_type !== "header" && slice.slice_type !== "header_spotlight")
  ) {
    return false;
  }

  const normalize = (val?: string) => val?.trim().replace(/\/$/, "") || "";
  const normalizedStartUrl = normalize(startUrl);

  const checkBlocks = (blocks: any[]) =>
    blocks.some((block: any) => {
      const hasMatchingLink = block.spans?.some((span: any) => {
        if (span.type === "hyperlink") {
          const linkUrl = span.data?.url || linkResolver(span.data);
          return normalize(linkUrl) === normalizedStartUrl;
        }
        return false;
      });

      return hasMatchingLink;
    });

  const buttonsRaw = slice.primary?.buttons?.raw || [];
  const contentRaw = slice.primary?.content?.raw || [];

  return checkBlocks(buttonsRaw) || checkBlocks(contentRaw);
};
